import { HttpClient } from '../api/client'
import { TokenManager } from '../auth/tokenManager'
import type { Notification, NotificationKind } from '../../types/notifications'

/**
 * Wire-format row returned by the backend (snake_case, ISO strings).
 * Mirrors NotificationRow in backend/src/services/notificationService.ts.
 */
export interface NotificationApiRow {
  id: string
  user_id: string
  kind: NotificationKind
  title: string
  body: string | null
  link: string | null
  data: Record<string, unknown> | null
  created_at: string
  read_at: string | null
}

interface ListResponse {
  notifications: NotificationApiRow[]
}

export function rowToNotification(row: NotificationApiRow): Notification {
  return {
    id: row.id,
    kind: row.kind,
    title: row.title,
    body: row.body ?? undefined,
    link: row.link ?? undefined,
    data: row.data ?? undefined,
    createdAt: row.created_at,
    readAt: row.read_at,
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  return TokenManager.getAuthHeaders()
}

export const NotificationApiClient = {
  async list(): Promise<Notification[]> {
    const headers = await authHeaders()
    const response = await HttpClient.get<ListResponse>('/notifications', headers)
    if (response.error || !response.data) {
      throw new Error(response.error?.message ?? 'Failed to list notifications')
    }
    return response.data.notifications.map(rowToNotification)
  },

  async markRead(id: string): Promise<void> {
    const headers = await authHeaders()
    const response = await HttpClient.patch<{ success: true }>(
      `/notifications/${id}/read`,
      {},
      headers
    )
    if (response.error) {
      throw new Error(response.error.message)
    }
  },

  async markAllRead(): Promise<void> {
    const headers = await authHeaders()
    const response = await HttpClient.post<{ success: true }>(
      '/notifications/read-all',
      {},
      headers
    )
    if (response.error) {
      throw new Error(response.error.message)
    }
  },

  async remove(id: string): Promise<void> {
    const headers = await authHeaders()
    const response = await HttpClient.delete<{ success: true }>(`/notifications/${id}`, headers)
    if (response.error) {
      throw new Error(response.error.message)
    }
  },

  async clear(): Promise<void> {
    const headers = await authHeaders()
    const response = await HttpClient.delete<{ success: true }>('/notifications', headers)
    if (response.error) {
      throw new Error(response.error.message)
    }
  },
}
